/**
 * A lead's returns, on its page: each buyer's sale with its return window,
 * and every return asked for or made, newest first.
 */
import { Alert, Badge, Group, Loader, Stack, Table, Text } from '@mantine/core';
import { useLeadReturns } from '../../api/returns';
import { formatDateTime, formatGBP } from '../../theme';
import { useVocab } from '../../vocab';
import { windowWords } from './ReturnDialog';
import { ReasonCell, ReturnStatusBadge, WhoCell, returnAmount } from './returnsShared';

export function LeadReturnsPanel({ leadId }: { leadId: string }) {
  const v = useVocab();
  const { data, isPending, error } = useLeadReturns(leadId);
  if (isPending) return <Group justify="center" p="md"><Loader size="sm" /></Group>;
  if (error) return <Alert color="red" variant="light">{error.message}</Alert>;
  if (!data) return null;
  return (
    <Stack gap="md">
      <Stack gap={6}>
        <Text size="sm" fw={600}>{data.sales.length === 1 ? 'Sale' : 'Sales'}</Text>
        {data.sales.length === 0 && <Text size="sm" c="dimmed">{`Not ${v.p.sold}.`}</Text>}
        {data.sales.map((s) => (
          <Group key={s.buyer_id} gap="sm" wrap="wrap">
            <Text size="sm" fw={500}>{s.buyer_name}</Text>
            <Text size="sm" ff="monospace">{formatGBP(s.price)}</Text>
            {s.sold_at && <Text size="xs" c="dimmed">{formatDateTime(s.sold_at)}</Text>}
            {s.returned ? (
              <Badge size="sm" variant="light" color="gray">Returned</Badge>
            ) : (
              <Badge size="sm" variant="light" color={s.window_open ? 'teal' : 'orange'}>{windowWords(s)}</Badge>
            )}
            {s.return_status === 'requested' && <Badge size="sm" variant="light" color="blue">Return requested</Badge>}
          </Group>
        ))}
      </Stack>
      <Stack gap={6}>
        <Text size="sm" fw={600}>Returns</Text>
        {data.returns.length === 0 ? (
          <Text size="sm" c="dimmed">{`No returns of this ${v.one('lead')}.`}</Text>
        ) : (
          <Table.ScrollContainer minWidth={560}>
            <Table verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{v.One('buyer')}</Table.Th>
                  <Table.Th>Reason</Table.Th>
                  <Table.Th>Amount</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>History</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.returns.map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td><Text size="sm">{r.buyer_name}</Text></Table.Td>
                    <Table.Td><ReasonCell r={r} /></Table.Td>
                    <Table.Td><Text size="sm" ff="monospace">{returnAmount(r)}</Text></Table.Td>
                    <Table.Td><ReturnStatusBadge status={r.status} /></Table.Td>
                    <Table.Td><WhoCell r={r} /></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </Stack>
  );
}
